import { Plus, Trash2, SlidersHorizontal, Table2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";

const MODES = [
  { id: "manual", label: "Manual", note: "You type the exact Standard and Express prices. Every order is charged them — no free-shipping threshold.", Icon: SlidersHorizontal },
  { id: "automatic", label: "Automatic — rate table", note: "Prices step up with the number of vials in the order. Standard ships free at or above your threshold.", Icon: Table2 },
];

export const ShippingSettingsCard = ({ form, setForm, set }) => {
  const mode = form.shipping_mode || "automatic";
  const expressOn = form.express_shipping_enabled ?? true;
  const table = form.shipping_rate_table || [];

  const setMode = (id) => setForm((f) => {
    const next = { ...f, shipping_mode: id };
    if (id === "automatic" && !(f.shipping_rate_table || []).length) {
      next.shipping_rate_table = [{ min_qty: 1, standard: f.shipping_fee, express: f.express_shipping_fee ?? 30, _key: crypto.randomUUID() }];
    }
    return next;
  });
  const setTier = (idx, key, val) => setForm((f) => ({
    ...f, shipping_rate_table: f.shipping_rate_table.map((t, i) => (i === idx ? { ...t, [key]: val } : t)),
  }));
  const addTier = () => setForm((f) => ({ ...f, shipping_rate_table: [...(f.shipping_rate_table || []), { min_qty: "", standard: "", express: "", _key: crypto.randomUUID() }] }));
  const removeTier = (idx) => setForm((f) => ({ ...f, shipping_rate_table: f.shipping_rate_table.filter((_, i) => i !== idx) }));

  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white p-8" data-testid="admin-shipping-settings">
      <h2 className="font-display text-lg uppercase tracking-wide text-[#1A1A1A] mb-2">Shipping</h2>
      <p className="text-sm text-[#6E675F] mb-6">
        Customers pick <strong>Standard</strong> (Canada Post Expedited Parcel™) or <strong>Express</strong> (Canada Post Xpresspost™) at checkout.
        Choose how the prices are set:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8" data-testid="shipping-mode-picker">
        {MODES.map(({ id, label, note, Icon }) => (
          <button
            key={id}
            type="button"
            data-testid={`shipping-mode-${id}`}
            aria-pressed={mode === id}
            onClick={() => setMode(id)}
            className={`text-left border-2 rounded-lg p-4 transition-colors ${mode === id ? "border-[#8C7B6B] bg-white" : "border-[#DDD7CF] bg-white/60 hover:border-[#8C7B6B]/50"}`}
          >
            <span className="flex items-center gap-2.5">
              <Icon className={`w-5 h-5 ${mode === id ? "text-[#8C7B6B]" : "text-[#6E675F]"}`} />
              <span className="text-sm font-semibold text-[#1A1A1A]">{label}</span>
              <span className={`ml-auto w-4 h-4 rounded-full border-2 ${mode === id ? "border-[#8C7B6B] bg-[#8C7B6B]" : "border-[#DDD7CF]"}`} />
            </span>
            <span className="block text-xs text-[#6E675F] mt-1.5">{note}</span>
          </button>
        ))}
      </div>

      {mode === "manual" ? (
        <div data-testid="shipping-manual-fields">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] mb-3">Prices charged on every order</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="s-shipfee">Standard Price (CAD)</Label>
              <Input id="s-shipfee" type="number" min="0" step="0.5" data-testid="settings-shipping-fee" value={form.shipping_fee} onChange={set("shipping_fee")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="s-expfee">Express Price (CAD)</Label>
              <Input id="s-expfee" type="number" min="0" step="0.5" data-testid="settings-express-fee" value={form.express_shipping_fee ?? 30} onChange={set("express_shipping_fee")} disabled={!expressOn} className="mt-1 disabled:opacity-50" />
            </div>
          </div>
        </div>
      ) : (
        <div data-testid="shipping-automatic-fields">
          <div className="max-w-xs mb-6">
            <Label htmlFor="s-threshold">Free Standard Shipping From (CAD)</Label>
            <Input id="s-threshold" type="number" min="0" step="1" data-testid="settings-free-shipping-threshold" value={form.free_shipping_threshold} onChange={set("free_shipping_threshold")} className="mt-1" />
            <p className="text-[11px] text-[#6E675F] mt-1">Merchandise total after bulk discounts. Express is always charged.</p>
          </div>

          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] mb-3">Rate table by order size</p>
          <div className="space-y-3" data-testid="shipping-rate-table">
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 text-xs tracking-[0.1em] uppercase font-semibold text-[#6E675F]">
              <span>From (vials)</span><span>Standard $</span><span>Express $</span><span className="w-10"></span>
            </div>
            {table.map((t, i) => (
              <div key={t._key || i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center" data-testid={`shipping-tier-${i}`}>
                <Input type="number" min="1" step="1" value={t.min_qty} onChange={(e) => setTier(i, "min_qty", e.target.value)} data-testid={`shipping-tier-qty-${i}`} placeholder="e.g. 1" />
                <Input type="number" min="0" step="0.5" value={t.standard} onChange={(e) => setTier(i, "standard", e.target.value)} data-testid={`shipping-tier-standard-${i}`} placeholder="e.g. 15" />
                <Input type="number" min="0" step="0.5" value={t.express} onChange={(e) => setTier(i, "express", e.target.value)} data-testid={`shipping-tier-express-${i}`} placeholder="e.g. 25" disabled={!expressOn} className="disabled:opacity-50" />
                <Button size="icon" variant="outline" onClick={() => removeTier(i)} data-testid={`shipping-tier-remove-${i}`} className="text-red-700 border-red-200 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {table.length === 0 && (
              <p className="text-sm text-[#6E675F]" data-testid="shipping-rate-table-empty">No tiers — the fallback prices below are used for every order size.</p>
            )}
            <Button variant="outline" onClick={addTier} data-testid="add-shipping-tier-button" className="mt-1">
              <Plus className="w-4 h-4 mr-1" /> Add Tier
            </Button>
          </div>
          <p className="text-[11px] text-[#6E675F] mt-3">
            The highest matching tier wins (e.g. tiers at 1, 4 and 11 vials: a 6-vial order uses the 4+ row). Bundles count each vial inside them.
          </p>

          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] mt-6 mb-3">Fallback prices (when no tier matches)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="s-shipfee">Standard (CAD)</Label>
              <Input id="s-shipfee" type="number" min="0" step="0.5" data-testid="settings-shipping-fee" value={form.shipping_fee} onChange={set("shipping_fee")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="s-expfee">Express (CAD)</Label>
              <Input id="s-expfee" type="number" min="0" step="0.5" data-testid="settings-express-fee" value={form.express_shipping_fee ?? 30} onChange={set("express_shipping_fee")} disabled={!expressOn} className="mt-1 disabled:opacity-50" />
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-8 mb-3">
        <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B]">Delivery estimates &amp; Express availability</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6E675F]">Express {expressOn ? "offered" : "hidden"}</span>
          <Switch checked={expressOn} onCheckedChange={(v) => setForm((f) => ({ ...f, express_shipping_enabled: v }))} data-testid="settings-express-enabled" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="s-std-eta">Standard · Expedited Parcel™</Label>
          <Input id="s-std-eta" data-testid="settings-standard-eta" value={form.standard_shipping_eta ?? ""} onChange={set("standard_shipping_eta")} placeholder="2–8 business days" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="s-exp-eta">Express · Xpresspost™</Label>
          <Input id="s-exp-eta" data-testid="settings-express-eta" value={form.express_shipping_eta ?? ""} onChange={set("express_shipping_eta")} placeholder="1–2 business days" disabled={!expressOn} className="mt-1 disabled:opacity-50" />
        </div>
      </div>
      <p className="text-[11px] text-[#6E675F] mt-3">
        Canada Post pricing varies by weight and zone — check canadapost.ca when setting your prices (Expedited Parcel™ ≈ 2–8 business days, Xpresspost™ ≈ 1–2 nationally).
      </p>

      <div className="mt-6">
        <Label htmlFor="s-shipfrom">Ship-From Address (printed on shipping labels)</Label>
        <Textarea id="s-shipfrom" data-testid="settings-ship-from" value={form.ship_from || ""} onChange={set("ship_from")} rows={3} className="mt-1" placeholder={"Eternal Biolabs\n123 Example St\nCity, Province  A1A 1A1"} />
      </div>
    </div>
  );
};
